import { useEffect, useState } from 'react';
import Logo from '../Logo';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import downloadAndroid from '../../assets/images/downloadAndroid.png';
import downloadIos from '../../assets/images/downloadIos.png';
import { useIsMobile } from '../../utils/hooks';
import { Button, Drawer, Modal } from 'antd';
import {
  AddToHomeScreen,
  ArrowUpFromBracketSolid,
  EllipsisVerticalSolid,
  PlusSolid,
  XmarkSolid,
} from '../../utils/icons';
import { termsTypes, TermsTypes } from '../../views/Terms/index';

const Footer = () => {
  const { t } = useTranslation();
  const [showInstructionsToInstallOnIos, setShowInstructionsToInstallOnIos] =
    useState<boolean>(false);
  const [showInstructionsToInstallOnAndroid, setShowInstructionsToInstallOnAndroid] =
    useState<boolean>(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState<boolean>(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const scriptToNewsletterForm = document.createElement('script');
    scriptToNewsletterForm.src = 'https://app.mailjet.com/pas-nc-embedded-v1.js';
    scriptToNewsletterForm.type = 'text/javascript';
    document.body.appendChild(scriptToNewsletterForm);

    return () => {
      document.body.removeChild(scriptToNewsletterForm);
    };
  }, []);

  const getInstructionsToInstallOnIos = () => (
    <ol className="m-0 pl-[20px]">
      <li className="mb-[16px]">
        <div className="flex justify-between">
          {t('footer.instructionsToInstallOnIos.step1')}{' '}
          <ArrowUpFromBracketSolid className="[&>path]:fill-info" />
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          {t('footer.instructionsToInstallOnIos.step2')}
          <div className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] bg-text2">
            <PlusSolid className="[&>path]:fill-text5" />
          </div>
        </div>
      </li>
    </ol>
  );

  const getInstructionsToInstallOnAndroid = () => (
    <ol className="m-0 pl-[20px]">
      <li className="mb-[16px]">
        <div className="flex justify-between">
          {t('footer.instructionsToInstallOnAndroid.step1')} <EllipsisVerticalSolid />
        </div>
      </li>
      <li className="mb-[16px]">
        <div className="flex justify-between">
          {t('footer.instructionsToInstallOnAndroid.step2')} <AddToHomeScreen />
        </div>
      </li>
      <li>{t('footer.instructionsToInstallOnAndroid.step3')}</li>
    </ol>
  );

  const modalContent = showNewsletterModal ? (
    <div className="h-[calc(100vh-200px)]">
      <iframe
        data-w-type="embedded"
        src="https://ss9g3.mjt.lu/wgt/ss9g3/xo42/form?c=b234ad39"
        width="100%"
        title="Newsletter Form"
        style={{ border: 'none', height: '100%', padding: '0' }}
      />
    </div>
  ) : showInstructionsToInstallOnIos ? (
    getInstructionsToInstallOnIos()
  ) : showInstructionsToInstallOnAndroid ? (
    getInstructionsToInstallOnAndroid()
  ) : (
    <></>
  );

  const modalTitle = showNewsletterModal
    ? t('footer.links.8')
    : showInstructionsToInstallOnIos
      ? t('footer.instructionsToInstallOnIos.title')
      : showInstructionsToInstallOnAndroid
        ? t('footer.instructionsToInstallOnAndroid.title')
        : '';

  const onCloseModal = () => {
    setShowInstructionsToInstallOnIos(false);
    setShowInstructionsToInstallOnAndroid(false);
    setShowNewsletterModal(false);
  };

  return (
    <>
      <Modal
        title={modalTitle}
        open={
          (showInstructionsToInstallOnIos ||
            showInstructionsToInstallOnAndroid ||
            showNewsletterModal) &&
          !isMobile
        }
        onCancel={onCloseModal}
        footer={null}
        closeIcon={<XmarkSolid />}
        centered
      >
        {modalContent}
      </Modal>
      <Drawer
        size={showNewsletterModal ? 'large' : 'default'}
        title={modalTitle}
        placement="bottom"
        onClose={onCloseModal}
        open={
          (showInstructionsToInstallOnIos ||
            showInstructionsToInstallOnAndroid ||
            showNewsletterModal) &&
          isMobile
        }
        closeIcon={<XmarkSolid />}
      >
        {modalContent}
      </Drawer>

      <div className="z-10 box-border flex w-full flex-col items-center bg-secondary60 px-[24px] pt-[24px] text-text5 md:pt-[32px]">
        <div className="flex w-[100%] max-w-[1200px] flex-col items-start">
          <div className="flex flex-col md:flex-row md:items-end">
            <div className="flex-1">
              <Logo />
              <p className="mb-[32px] mt-[16px] max-w-[364px] text-text5 md:mb-0">
                {t('footer.title')}
              </p>
            </div>
            <div className="flex-1">
              <div className="flex flex-col flex-wrap justify-end gap-[8px] md:flex-row [&_a]:whitespace-nowrap [&_a]:px-[4px] [&_a]:text-text5">
                {[
                  { title: t('price.title'), to: '/home/price' },
                  // { title: t('footer.links.2'), to: 'https://dev.animal-heaven.org/' },
                  { title: t('footer.links.3'), to: '/home/contact-us' },
                ].map((e) => (
                  <Link to={e.to || ''} key={e.title}>
                    {e.title}
                  </Link>
                ))}
                <Button
                  type="link"
                  className="flex h-auto justify-start border-none px-[4px] text-text5 [&>span]:underline"
                  onClick={() => setShowNewsletterModal(true)}
                >
                  {t('footer.links.8')}
                </Button>
                {termsTypes.map((type: TermsTypes, index: number) => (
                  <Link to={`/terms?termType=${type}`} target="blank" key={`terms-${index}`}>
                    {t(`footer.links.${index + 4}`)}
                  </Link>
                ))}
              </div>
              <div className="mt-[40px] flex justify-center md:mt-[16px] md:justify-end">
                <img
                  src={downloadAndroid}
                  alt=""
                  className="mr-[8px] cursor-pointer"
                  onClick={() => setShowInstructionsToInstallOnAndroid(true)}
                />
                <img
                  src={downloadIos}
                  alt=""
                  className="cursor-pointer"
                  onClick={() => setShowInstructionsToInstallOnIos(true)}
                />
              </div>
            </div>
          </div>
        </div>
        <p className="pb-[48px] pt-[40px] text-text5 md:pt-[32px]">{t('footer.bottomText')}</p>
      </div>
    </>
  );
};

export default Footer;
